import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, ExternalLink, Sparkles, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Free national resources stay inline — they rarely change and aren't affiliate items.
const resources = [
  {
    id: "alz-24-7",
    title: "Alzheimer's Association 24/7 Helpline",
    description: "Free, confidential support any time of day or night — for questions, crisis moments, or just needing to talk to someone who understands.",
    detail: "1-800-272-3900",
    href: "https://www.alz.org/help-support/resources/helpline",
  },
  {
    id: "eldercare-locator",
    title: "Eldercare Locator (U.S. Administration on Aging)",
    description: "Free federal service that connects families to local Area Agencies on Aging, transportation, meals, in-home services, and elder-abuse reporting.",
    detail: "1-800-677-1116 · eldercare.acl.gov",
    href: "https://eldercare.acl.gov/",
  },
  {
    id: "caregiver-action",
    title: "Caregiver Action Network",
    description: "Peer forums, video resource library, and a family caregiver toolbox — practical help for the 53 million Americans caring for a loved one.",
    detail: "caregiveraction.org",
    href: "https://www.caregiveraction.org/",
  },
  {
    id: "aarp-caregiving",
    title: "AARP Family Caregiving Guide",
    description: "State-by-state legal, financial, and long-distance caregiving guides. Free downloadable checklists for hospital-to-home transitions and family meetings.",
    detail: "aarp.org/caregiving",
    href: "https://www.aarp.org/caregiving/",
  },
];

export default function BooksPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("books");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/extras/books`)
      .then((res) => setBooks(res.data))
      .catch(() => setBooks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="books-back-btn"
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-primary hover:bg-primary/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <button
              data-testid="books-brand-home"
              onClick={() => navigate("/get-started")}
              className="flex items-center gap-3"
              aria-label="Explore care types"
            >
              <img
                src="/tyvena-lockup-horizontal.png"
                alt="Tyvena"
                className="h-10 md:h-11 w-auto rounded-lg shadow-md"
              />
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full mb-6">
          <Sparkles className="w-4 h-4 text-secondary" />
          <span className="text-sm font-body font-semibold text-text-primary">
            Curated by Tyvena
          </span>
        </div>
        <div className="flex items-start gap-4 md:gap-6">
          <div className="hidden md:flex flex-shrink-0 w-16 h-16 rounded-2xl bg-primary/10 items-center justify-center">
            <BookOpen className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary leading-tight mb-4">
              Recommended Books &amp; Resources
            </h1>
            <p className="text-lg text-text-secondary font-body max-w-2xl leading-relaxed">
              A short, honest shelf — books that hospice nurses, geriatric doctors, and
              families have quietly kept passing to each other for years. Plus a handful of
              free national resources most families don't know exist.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-10 inline-flex bg-white rounded-full p-1 border border-[#E2E8F0] shadow-sm">
          <button
            data-testid="tab-books"
            onClick={() => setActiveTab("books")}
            className={`px-6 py-2 rounded-full font-body font-semibold text-sm transition-all ${
              activeTab === "books"
                ? "bg-primary text-white shadow-md"
                : "text-text-secondary hover:text-primary"
            }`}
          >
            Books ({books.length})
          </button>
          <button
            data-testid="tab-resources"
            onClick={() => setActiveTab("resources")}
            className={`px-6 py-2 rounded-full font-body font-semibold text-sm transition-all ${
              activeTab === "resources"
                ? "bg-primary text-white shadow-md"
                : "text-text-secondary hover:text-primary"
            }`}
          >
            Free Resources ({resources.length})
          </button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {activeTab === "books" ? (
          loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : books.length === 0 ? (
            <p className="text-center text-text-secondary font-body py-16">No books to show right now.</p>
          ) : (
            <div data-testid="books-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {books.map((b) => (
                <a
                  key={b.id}
                  data-testid={`book-${b.id}`}
                  href={b.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white rounded-2xl border border-[#E2E8F0] shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all overflow-hidden flex flex-col"
                >
                  <div className="aspect-[2/3] overflow-hidden bg-gradient-to-br from-primary/10 to-primary/20 flex items-center justify-center relative">
                    <span className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm font-heading font-semibold text-primary/60 leading-tight">
                      {b.title}
                    </span>
                    {b.cover_url && (
                      <img
                        src={b.cover_url}
                        alt={b.title}
                        className="relative w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    )}
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    {b.topic && (
                      <p className="text-[11px] font-body font-bold uppercase tracking-wider text-primary mb-1.5">
                        {b.topic}
                      </p>
                    )}
                    <h3 className="text-base font-heading font-semibold text-text-primary leading-snug">
                      {b.title}
                    </h3>
                    {b.author && (
                      <p className="text-sm text-text-secondary font-body italic mt-1 mb-3">
                        {b.author}
                      </p>
                    )}
                    <p className="text-xs text-text-secondary font-body leading-relaxed flex-1">
                      {b.description}
                    </p>
                    <div className="flex items-center gap-1.5 text-xs font-body font-semibold text-primary mt-4 group-hover:gap-2 transition-all">
                      View on Amazon
                      <ExternalLink className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )
        ) : (
          <div data-testid="resources-list" className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {resources.map((r) => (
              <a
                key={r.id}
                data-testid={`resource-${r.id}`}
                href={r.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl border border-[#E2E8F0] shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all p-6 border-l-4 border-l-secondary flex flex-col"
              >
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-2 leading-snug">
                  {r.title}
                </h3>
                <p className="text-sm text-text-secondary font-body leading-relaxed mb-4">
                  {r.description}
                </p>
                <div className="flex items-center justify-between gap-4 mt-auto">
                  <span className="text-sm font-body font-bold text-primary">{r.detail}</span>
                  <ExternalLink className="w-4 h-4 text-primary group-hover:translate-x-0.5 transition-transform" />
                </div>
              </a>
            ))}
          </div>
        )}

        <p className="text-xs text-text-secondary font-body text-center mt-10 max-w-2xl mx-auto">
          Amazon links open in a new tab. <strong>As an Amazon Associate, Tyvena earns from
          qualifying purchases</strong> — at no extra cost to you. These titles are here because
          they're worth reading.
        </p>
      </section>
    </div>
  );
}
